/**
 * Client Pricing Manager Component - Premium UI Redesign
 * Admin/Accounting interface for managing client-specific product pricing.
 * 
 * Features:
 * - View/Edit pricing per client code and product
 * - Effective date support
 * - Bulk import from CSV
 * - Copy pricing from another client
 * - Audit log viewing
 * 
 * UI Enhancements:
 * - Back to Dashboard navigation
 * - Premium card containers
 * - Enhanced client selection
 * - Polished tabs
 * - Designed empty states
 * - Consistent typography & spacing
 * 
 * Access: Accounting and Admin roles only
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Upload, 
  History,
  Building2,
  Package,
  Calendar,
  ArrowLeft,
  RefreshCw,
  FileText,
  Search,
  X,
  Sparkles,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  MapPin
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAuth } from '../../App';
import { StatePricingMatrix } from './StatePricingMatrix';
import DropdownDiagOverlay from './DropdownDiagOverlay';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Status Badge Component
const StatusBadge = ({ active }) => {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-500 border border-slate-500/20">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      Inactive
    </span>
  );
};

// Action Badge Component
const ActionBadge = ({ action }) => {
  const configs = {
    created: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20' },
    updated: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20' },
    deleted: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/20' }
  };
  const config = configs[action] || configs.updated;
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${config.bg} ${config.text} ${config.border}`}>
      {action}
    </span>
  );
};

// Winning-source badge for the Effective Pricing panel (PRICING-RESYNC-001 Phase 4)
const SourceBadge = ({ source }) => {
  const configs = {
    engine_rule: { label: 'Engine Rule Wins', cls: 'bg-blue-500/10 text-blue-700 border-blue-500/30' },
    legacy_rule: { label: 'Legacy Rule Wins', cls: 'bg-amber-500/10 text-amber-700 border-amber-500/30' },
    product_default: { label: 'Product Default', cls: 'bg-slate-500/10 text-slate-600 border-slate-500/30' },
    missing_pricing: { label: 'Missing Pricing', cls: 'bg-red-500/10 text-red-600 border-red-500/30' },
  };
  const c = configs[source] || configs.product_default;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${c.cls}`}>
      {c.label}
    </span>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, title, description, action, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6">
    <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mb-5 shadow-inner">
      <Icon className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-slate-700 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 text-center max-w-sm mb-5">{description}</p>
    {action && (
      <Button onClick={onAction} className="bg-blue-600 hover:bg-blue-700">
        {actionLabel}
      </Button>
    )}
  </div>
);

/**
 * Searchable client card grid — shared by the Client Pricing and State
 * Pricing Matrix tabs. `accent` switches the highlight palette so each
 * tab keeps its theme (blue = pricing, emerald = state matrix).
 */
const ClientCardGrid = ({ clientCodes, selectedClient, onSelect, pricingSummary, search, onSearchChange, accent = 'blue', testIdPrefix = '' }) => {
  const palette = accent === 'emerald'
    ? {
        focusInput: 'focus-visible:ring-emerald-500/20',
        selected: 'border-emerald-500 bg-emerald-50/70 ring-2 ring-emerald-500/30 shadow-sm',
        hover: 'hover:border-emerald-300',
        focusCard: 'focus-visible:ring-emerald-500/40',
        selectedText: 'text-emerald-700',
        check: 'text-emerald-600',
      }
    : {
        focusInput: 'focus-visible:ring-blue-500/20',
        selected: 'border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/30 shadow-sm',
        hover: 'hover:border-blue-300',
        focusCard: 'focus-visible:ring-blue-500/40',
        selectedText: 'text-blue-700',
        check: 'text-blue-600',
      };

  const term = search.trim().toLowerCase();
  const filtered = term
    ? clientCodes.filter(c =>
        (c.code || '').toLowerCase().includes(term) ||
        (c.client_name || '').toLowerCase().includes(term))
    : clientCodes;

  return (
    <div className="space-y-4">
      {/* Quick search — filters the card grid below */}
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search clients by code or name..."
          className={`pl-9 h-10 border-slate-200 focus-visible:ring-2 ${palette.focusInput}`}
          data-testid={`${testIdPrefix}client-search-input`}
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            data-testid={`${testIdPrefix}client-search-clear`}
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Client card grid */}
      {clientCodes.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">Loading clients...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-2" data-testid={`${testIdPrefix}client-grid-empty`}>
          No clients match "{search}"
        </p>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[420px] overflow-y-auto pr-1"
          data-testid={`${testIdPrefix}client-card-grid`}
        >
          {filtered.map(client => {
            const isSelected = selectedClient?.id === client.id;
            const activeCount = pricingSummary[client.id] || 0;
            return (
              <button
                key={client.id}
                onClick={() => onSelect(client.id)}
                className={`text-left rounded-xl border p-3.5 transition-all duration-150 focus:outline-none focus-visible:ring-2 ${palette.focusCard} ${
                  isSelected
                    ? palette.selected
                    : `border-slate-200 bg-white ${palette.hover} hover:shadow-md hover:-translate-y-0.5`
                }`}
                data-testid={`${testIdPrefix}client-card-${client.code}`}
                aria-pressed={isSelected}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={`font-bold text-sm truncate ${isSelected ? palette.selectedText : 'text-slate-800'}`}>
                    {client.code}
                  </span>
                  {isSelected && <CheckCircle2 className={`w-4 h-4 ${palette.check} shrink-0`} />}
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5" title={client.client_name || ''}>
                  {client.client_name || '—'}
                </p>
                <div className="mt-2.5 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${activeCount > 0 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span className={`text-[11px] font-medium ${activeCount > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {activeCount > 0 ? `${activeCount} priced` : 'No pricing'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Client Pricing Manager Component
 */
export const ClientPricingManager = ({ readOnly = false }) => {   // LEGACY-PRICING-MIGRATION-001: readOnly hides legacy write actions (Add/Edit/Delete/Copy/Import)
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Access check
  const hasAccess = user?.role === 'admin' || user?.role === 'accounting';
  
  // State
  const [activeTab, setActiveTab] = useState('pricing');
  const [clientCodes, setClientCodes] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [pricingSummary, setPricingSummary] = useState({});
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [effectivePricing, setEffectivePricing] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  // PRICING-SELECT-FLICKER-001: portal Select content INTO the dialog node so
  // the Dialog's scroll-lock/inert layer can't fight the Select's dismissable
  // layer on real pointer streams (hover flicker hardening).
  const [addDialogNode, setAddDialogNode] = useState(null);
  const [copyDialogNode, setCopyDialogNode] = useState(null);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  
  // Helper to get today's date in YYYY-MM-DD format (local timezone)
  const getLocalDateStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    product_name: '',
    unit_price: '',
    effective_from: getLocalDateStr(),
    effective_to: '',
    notes: ''
  });
  
  // Copy dialog state
  const [copySourceClient, setCopySourceClient] = useState('');
  
  // Bulk import state
  const [importFile, setImportFile] = useState(null);
  
  // Fetch client codes on mount
  useEffect(() => {
    if (hasAccess) {
      fetchClientCodes();
      fetchProducts();
      fetchPricingSummary();
    }
  }, [hasAccess]);
  
  // Fetch pricing when client is selected
  useEffect(() => {
    if (selectedClient) {
      fetchClientPricing(selectedClient.id);
    }
  }, [selectedClient]);
  
  const fetchClientCodes = async () => {
    try {
      const response = await axios.get(`${API}/client-codes`);
      setClientCodes(response.data || []);
    } catch (error) {
      console.error('Error fetching client codes:', error);
      toast.error('Failed to load client codes');
    }
  };
  
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/pricing/products`);
      setProducts(response.data?.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      try {
        const fallbackResponse = await axios.get(`${API}/client-products`);
        setProducts(fallbackResponse.data?.products || []);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  };
  
  // Per-client active pricing counts — drives the "Priced / No pricing" card badges
  const fetchPricingSummary = async () => {
    try {
      const response = await axios.get(`${API}/pricing/clients-summary`);
      setPricingSummary(response.data?.summary || {});
    } catch (error) {
      console.error('Error fetching pricing summary:', error);
    }
  };

  const fetchClientPricing = async (clientId) => {
    setIsLoading(true);
    try {
      const [pricingRes, effectiveRes] = await Promise.all([
        axios.get(`${API}/pricing/client/${clientId}?include_inactive=true`),
        axios.get(`${API}/pricing/client/${clientId}/effective`),
      ]);
      setPricing(pricingRes.data?.pricing || []);
      setEffectivePricing(effectiveRes.data?.products || []);
      fetchPricingSummary(); // keep card badges in sync after mutations
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing');
      setPricing([]);
      setEffectivePricing([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchAuditLogs = async (clientCode = null) => {
    setIsLoading(true);
    try {
      let url = `${API}/pricing/audit-log?limit=100`;
      if (clientCode) {
        url += `&client_code=${clientCode}`;
      }
      const response = await axios.get(url);
      setAuditLogs(response.data?.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClientSelect = (clientId) => {
    const client = clientCodes.find(c => c.id === clientId);
    setSelectedClient(client);
  };
  
  const handleAddPricing = () => {
    setEditingPricing(null);
    setFormData({
      product_id: '',
      product_name: '',
      unit_price: '',
      effective_from: getLocalDateStr(),
      effective_to: '',
      notes: ''
    });
    setShowAddDialog(true);
  };
  
  const handleEditPricing = (pricingItem) => {
    setEditingPricing(pricingItem);
    setFormData({
      product_id: pricingItem.product_id,
      product_name: pricingItem.product_name,
      unit_price: pricingItem.unit_price?.toString() || '',
      effective_from: pricingItem.effective_from?.split('T')[0] || '',
      effective_to: pricingItem.effective_to?.split('T')[0] || '',
      notes: pricingItem.notes || ''
    });
    setShowAddDialog(true);
  };
  
  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    setFormData(prev => ({
      ...prev,
      product_id: productId,
      product_name: product?.name || ''
    }));
  };
  
  const handleSubmitPricing = async () => {
    if (!formData.product_id || !formData.unit_price) {
      toast.error('Product and Unit Price are required');
      return;
    }
    
    const price = parseFloat(formData.unit_price);
    if (isNaN(price) || price < 0) {
      toast.error('Unit Price must be a valid positive number');
      return;
    }
    
    setIsLoading(true);
    try {
      // Send dates as YYYY-MM-DD strings to avoid timezone shifting
      // Backend will parse as date-only (no time component drift)
      if (editingPricing) {
        await axios.patch(`${API}/pricing/${editingPricing.id}`, {
          unit_price: price,
          effective_from: formData.effective_from || null,
          effective_to: formData.effective_to || null,
          notes: formData.notes
        });
        toast.success('Pricing updated successfully');
      } else {
        await axios.post(`${API}/pricing`, {
          client_code_id: selectedClient.id,
          product_id: formData.product_id,
          product_name: formData.product_name,
          unit_price: price,
          effective_from: formData.effective_from || null,
          effective_to: formData.effective_to || null,
          notes: formData.notes
        });
        toast.success('Pricing created successfully');
      }
      
      setShowAddDialog(false);
      fetchClientPricing(selectedClient.id);
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm('Are you sure you want to deactivate this pricing?')) {
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/pricing/${pricingId}`);
      toast.success('Pricing deactivated successfully');
      fetchClientPricing(selectedClient.id);
    } catch (error) {
      console.error('Error deleting pricing:', error);
      toast.error('Failed to deactivate pricing');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCopyPricing = async () => {
    if (!copySourceClient || !selectedClient) {
      toast.error('Please select a source client');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/pricing/copy?source_client_id=${copySourceClient}&target_client_id=${selectedClient.id}`);
      toast.success(`Copied ${response.data.copied} pricing items. ${response.data.skipped} skipped.`);
      setShowCopyDialog(false);
      fetchClientPricing(selectedClient.id);
    } catch (error) {
      console.error('Error copying pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to copy pricing');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBulkImport = async () => {
    if (!importFile || !selectedClient) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', importFile);
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/pricing/bulk-import?client_code_id=${selectedClient.id}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`Imported ${response.data.imported} pricing items`);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      setShowBulkImportDialog(false);
      setImportFile(null);
      fetchClientPricing(selectedClient.id);
    } catch (error) {
      console.error('Error importing pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to import pricing');
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return formatDateOnly(dateStr);
  };
  
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800">Access Denied</h2>
          <p className="text-slate-500 mt-2">Accounting or Admin role required.</p>
        </div>
      </div>
    );
  }
  
  return (
    <TooltipProvider>
      <div className="min-h-full bg-gradient-to-br from-slate-50 via-white to-slate-50" data-testid="client-pricing-manager">
        {/* TEMP diagnostic (PRICING-SELECT-FLICKER-001) — active only with ?dropdown_diag=1 */}
        {new URLSearchParams(window.location.search).get('dropdown_diag') === '1' && <DropdownDiagOverlay />}
        {/* Header */}
        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
          <div className="max-w-[1400px] mx-auto px-6 py-4">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 text-sm mb-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 transition-colors"
                data-testid="back-to-admin-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin</span>
              </button>
              <span className="text-slate-300">/</span>
              <button
                onClick={() => navigate('/admin/billing')}
                className="text-slate-500 hover:text-slate-900 transition-colors"
                data-testid="back-to-billing-btn"
              >
                Billing
              </button>
              <span className="text-slate-300">/</span>
              <span className="text-slate-900 font-medium">Client Pricing</span>
            </div>
            
            {/* Title */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="pricing-diagnostics-title">Pricing Diagnostics</h1>
                <p className="text-sm text-slate-500 mt-0.5">Admin / IT view — engine vs legacy rules, winning source, rule ids and resolution path. Business users manage prices in <a href="/admin/pricing" className="text-blue-600 underline">Client Selling Prices</a>.</p>
              </div>
              
              {/* Quick Stats */}
              <div className="hidden md:flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Clients</p>
                  <p className="text-lg font-semibold text-slate-700">{clientCodes.length}</p>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Products</p>
                  <p className="text-lg font-semibold text-slate-700">{products.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="h-11 p-1 bg-slate-100 rounded-lg inline-flex">
              <TabsTrigger
                value="pricing"
                className="px-6 h-9 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 rounded-md transition-all font-medium"
                data-testid="tab-pricing"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Client Pricing
              </TabsTrigger>
              <TabsTrigger
                value="state-pricing"
                className="px-6 h-9 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-emerald-600 rounded-md transition-all font-medium"
                data-testid="tab-state-pricing"
              >
                <MapPin className="w-4 h-4 mr-2" />
                State Pricing Matrix
              </TabsTrigger>
              <TabsTrigger
                value="audit"
                className="px-6 h-9 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 rounded-md transition-all font-medium"
                data-testid="tab-audit"
                onClick={() => fetchAuditLogs(selectedClient?.code)}
              >
                <History className="w-4 h-4 mr-2" />
                Audit Log
              </TabsTrigger>
            </TabsList>
            
            {/* Pricing Tab */}
            <TabsContent value="pricing" className="mt-0 space-y-6">
              {/* Client Selection Card */}
              <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-slate-800">Select Client</h2>
                      <p className="text-sm text-slate-500">Choose a client to view and manage product pricing</p>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-5">
                  <ClientCardGrid
                    clientCodes={clientCodes}
                    selectedClient={selectedClient}
                    onSelect={handleClientSelect}
                    pricingSummary={pricingSummary}
                    search={clientSearch}
                    onSearchChange={setClientSearch}
                    accent="blue"
                  />
                </div>
              </div>
              
              {/* Pricing Content */}
              {selectedClient ? (
                <>
                {/* Effective Pricing — Which Rule Wins (PRICING-RESYNC-001 Phase 4) */}
                <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden" data-testid="effective-pricing-panel">
                  <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50/60 to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md shadow-indigo-500/20">
                        <Sparkles className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">Effective Pricing — Source of Truth</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Billing resolves prices in this precedence: <span className="font-medium">Engine rules</span> (State/County → Client Default) → <span className="font-medium">Legacy rules</span> (this page) → <span className="font-medium">Product Default Price</span>. The badge shows which source actually wins per product.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="tf-thead">
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Engine Rule</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Legacy Rule</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective Price</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Winning Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {effectivePricing.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-8 text-center text-sm text-slate-400" data-testid="effective-pricing-empty">
                              No pricing rules configured for this client in either collection.
                            </td>
                          </tr>
                        ) : (
                          effectivePricing.map((row, idx) => (
                            <tr
                              key={row.product_id}
                              className={`border-b border-slate-100 last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                              data-testid={`effective-row-${row.product_id}`}
                            >
                              <td className="px-6 py-3.5">
                                <span className="font-medium text-slate-800 text-sm">{row.product_name}</span>
                                {row.engine_geo_rules_count > 0 && (
                                  <p className="text-[11px] text-indigo-500 mt-0.5">
                                    + {row.engine_geo_rules_count} state/county override{row.engine_geo_rules_count > 1 ? 's' : ''} (engine)
                                  </p>
                                )}
                              </td>
                              <td className="px-6 py-3.5 text-right tabular-nums text-sm">
                                {row.engine_default_rule ? (
                                  <span className={`font-semibold ${row.effective?.source === 'engine_rule' ? 'text-blue-700' : 'text-slate-600'}`}>
                                    {formatCurrency(row.engine_default_rule.price)}
                                  </span>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                              <td className="px-6 py-3.5 text-right tabular-nums text-sm">
                                {row.legacy_rule ? (
                                  <span className={`font-semibold ${row.effective?.source === 'legacy_rule' ? 'text-amber-700' : 'text-slate-400 line-through decoration-slate-300'}`}>
                                    {formatCurrency(row.legacy_rule.unit_price)}
                                  </span>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                              <td className="px-6 py-3.5 text-right">
                                <span className="font-bold text-emerald-600 tabular-nums">
                                  {formatCurrency(row.effective?.price)}
                                </span>
                              </td>
                              <td className="px-6 py-3.5">
                                <div className="flex flex-col gap-1">
                                  <span data-testid={`winner-badge-${row.product_id}`}>
                                    <SourceBadge source={row.effective?.source} />
                                  </span>
                                  <p className="text-[11px] text-slate-400 max-w-[320px] leading-snug">{row.effective?.reason}</p>
                                  {row.order_path?.binding_mismatch && (
                                    <div
                                      className="mt-1 px-2.5 py-1.5 rounded-md bg-red-50 border border-red-200 max-w-[320px]"
                                      data-testid={`binding-mismatch-${row.product_id}`}
                                    >
                                      <p className="text-[11px] font-semibold text-red-700 leading-snug">
                                        ⚠ Stale product binding — orders actually resolve to {formatCurrency(row.order_path.price)} ({row.order_path.resolution_level})
                                      </p>
                                      <p className="text-[11px] text-red-500 leading-snug mt-0.5">
                                        This rule is attached to an old product record. Orders resolve by label to a different (canonical) product with no rule. Add Pricing for this product again to bind it correctly, then deactivate this rule.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
                  {/* Pricing Header */}
                  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-800">
                          Pricing Rules for {selectedClient.code}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {pricing.filter(p => p.is_active).length} active
                        </Badge>
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
                          Legacy collection
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {selectedClient.client_name} — rules here act as <span className="font-medium">fallback</span> when no engine rule exists (see Effective Pricing above)
                      </p>
                    </div>
                    
                    {!readOnly && (
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowCopyDialog(true)}
                            className="h-9 border-slate-200"
                            data-testid="copy-pricing-btn"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copy pricing from another client</TooltipContent>
                      </Tooltip>
                      
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowBulkImportDialog(true)}
                            className="h-9 border-slate-200"
                            data-testid="bulk-import-btn"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Import
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Bulk import from CSV</TooltipContent>
                      </Tooltip>
                      
                      <Button
                        size="sm"
                        onClick={handleAddPricing}
                        className="h-9 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/20"
                        data-testid="add-pricing-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Pricing
                      </Button>
                    </div>
                    )}
                  </div>
                  
                  {/* Pricing Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="tf-thead">
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit Price</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective From</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective To</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricing.length === 0 ? (
                          <tr>
                            <td colSpan="6">
                              <EmptyState
                                icon={Package}
                                title="No pricing configured"
                                description="This client doesn't have any pricing rules yet. Add your first pricing rule to get started."
                                action={true}
                                actionLabel="Add First Pricing"
                                onAction={handleAddPricing}
                              />
                            </td>
                          </tr>
                        ) : (
                          pricing.map((item, idx) => (
                            <tr
                              key={item.id}
                              className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} ${!item.is_active ? 'opacity-50' : ''}`}
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                                    <Package className="w-4 h-4 text-slate-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium text-slate-800">{item.product_name}</span>
                                    {item.notes && (
                                      <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">{item.notes}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <span className="font-semibold text-emerald-600 tabular-nums">
                                  {formatCurrency(item.unit_price)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-1.5 text-sm text-slate-500">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {formatDate(item.effective_from)}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-slate-500">
                                  {item.effective_to ? formatDate(item.effective_to) : (
                                    <span className="text-slate-400 italic">Ongoing</span>
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <StatusBadge active={item.is_active} />
                              </td>
                              <td className="px-6 py-4">
                                {readOnly ? <span className="text-xs text-slate-400" data-testid="legacy-readonly-marker">read-only</span> : (
                                <div className="flex items-center justify-center gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditPricing(item)}
                                        disabled={!item.is_active}
                                        className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                                        data-testid={`edit-pricing-${item.id}`}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit pricing</TooltipContent>
                                  </Tooltip>
                                  
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeletePricing(item.id)}
                                        disabled={!item.is_active}
                                        className="h-8 w-8 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                                        data-testid={`delete-pricing-${item.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Deactivate pricing</TooltipContent>
                                  </Tooltip>
                                </div>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                </>
              ) : (
                /* Empty State - No Client Selected */
                <div className="bg-white rounded-xl border border-slate-200/60 border-dashed">
                  <EmptyState
                    icon={Building2}
                    title="No client selected"
                    description="Select a client above to view and manage their product pricing rules."
                  />
                </div>
              )}
            </TabsContent>
            
            {/* State Pricing Matrix Tab */}
            <TabsContent value="state-pricing" className="mt-0 space-y-6">
              {/* Client Selection Card (same as pricing tab) */}
              <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-slate-800">State-Based Pricing Matrix</h2>
                      <p className="text-sm text-slate-500">Manage hierarchical pricing by state and county</p>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-5">
                  <ClientCardGrid
                    clientCodes={clientCodes}
                    selectedClient={selectedClient}
                    onSelect={handleClientSelect}
                    pricingSummary={pricingSummary}
                    search={clientSearch}
                    onSearchChange={setClientSearch}
                    accent="emerald"
                    testIdPrefix="state-"
                  />
                </div>
              </div>
              
              {/* State Pricing Matrix Component */}
              <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden p-6">
                <StatePricingMatrix
                  clientId={selectedClient?.id}
                  clientCode={selectedClient?.code}
                  products={products}
                  onRuleChange={() => fetchClientPricing(selectedClient?.id)}
                />
              </div>
            </TabsContent>
            
            {/* Audit Log Tab */}
            <TabsContent value="audit" className="mt-0 space-y-6">
              <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
                {/* Audit Header */}
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                      <History className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-slate-800">Pricing Audit Log</h2>
                      <p className="text-sm text-slate-500">Track all pricing changes across clients</p>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAuditLogs(selectedClient?.code)}
                    disabled={isLoading}
                    className="h-9"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                
                {/* Filter */}
                <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-500">Filter by client:</span>
                    <Select
                      value={selectedClient?.id || 'all'}
                      onValueChange={(id) => {
                        if (id === 'all') {
                          setSelectedClient(null);
                          fetchAuditLogs(null);
                        } else {
                          const client = clientCodes.find(c => c.id === id);
                          setSelectedClient(client);
                          if (client) fetchAuditLogs(client.code);
                        }
                      }}
                    >
                      <SelectTrigger className="w-64 h-9 bg-white">
                        <SelectValue placeholder="All Clients" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clientCodes.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.code} — {client.client_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Audit Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="tf-thead">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Timestamp</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Price Change</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Changed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr>
                          <td colSpan="6">
                            <EmptyState
                              icon={History}
                              title="No audit logs found"
                              description="Pricing changes will appear here. Select a client and click Refresh to load logs."
                            />
                          </td>
                        </tr>
                      ) : (
                        auditLogs.map((log, idx) => (
                          <tr
                            key={log.id}
                            className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                {formatDate(log.timestamp)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-medium text-slate-700">{log.client_code}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-slate-600">{log.product_name}</span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <ActionBadge action={log.action} />
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {log.old_price !== null && (
                                  <span className="text-red-500 line-through text-sm tabular-nums">
                                    {formatCurrency(log.old_price)}
                                  </span>
                                )}
                                {log.old_price !== null && log.new_price !== null && (
                                  <span className="text-slate-300">→</span>
                                )}
                                {log.new_price !== null && (
                                  <span className="text-emerald-600 font-medium tabular-nums">
                                    {formatCurrency(log.new_price)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-slate-500">{log.changed_by_name}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Add/Edit Pricing Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-md" ref={setAddDialogNode}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingPricing ? (
                  <>
                    <Edit className="w-5 h-5 text-blue-500" />
                    Edit Pricing
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 text-blue-500" />
                    Add Pricing
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {editingPricing 
                  ? 'Update the pricing details below' 
                  : `Configure pricing for ${selectedClient?.code}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Product *</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={handleProductSelect}
                  disabled={!!editingPricing}
                >
                  <SelectTrigger className="h-10" data-testid="product-select">
                    <SelectValue placeholder="Select a product..." />
                  </SelectTrigger>
                  <SelectContent portalContainer={addDialogNode} className="data-[state=open]:animate-none data-[state=closed]:animate-none">
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Unit Price (USD) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unit_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit_price: e.target.value }))}
                    className="pl-8 h-10"
                    placeholder="0.00"
                    data-testid="unit-price-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Effective From</Label>
                  <Input
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                    className="h-10"
                    data-testid="effective-from-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Effective To</Label>
                  <Input
                    type="date"
                    value={formData.effective_to}
                    onChange={(e) => setFormData(prev => ({ ...prev, effective_to: e.target.value }))}
                    className="h-10"
                    placeholder="Optional"
                    data-testid="effective-to-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                  className="h-10"
                  data-testid="notes-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitPricing}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-pricing-btn"
              >
                {isLoading ? 'Saving...' : (editingPricing ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Copy Pricing Dialog */}
        <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
          <DialogContent className="sm:max-w-md" ref={setCopyDialogNode}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-purple-500" />
                Copy Pricing
              </DialogTitle>
              <DialogDescription>
                Copy all active pricing from another client to <span className="font-medium text-slate-700">{selectedClient?.code}</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Source Client</Label>
                <Select
                  value={copySourceClient}
                  onValueChange={setCopySourceClient}
                >
                  <SelectTrigger className="h-10" data-testid="copy-source-select">
                    <SelectValue placeholder="Select source client..." />
                  </SelectTrigger>
                  <SelectContent portalContainer={copyDialogNode} className="data-[state=open]:animate-none data-[state=closed]:animate-none">
                    {clientCodes
                      .filter(c => c.id !== selectedClient?.id)
                      .map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.code} — {client.client_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 inline mr-1.5" />
                  Existing pricing for the same products will be skipped (not overwritten).
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCopyDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCopyPricing}
                disabled={isLoading || !copySourceClient}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-copy-btn"
              >
                {isLoading ? 'Copying...' : 'Copy Pricing'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Bulk Import Dialog */}
        <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-500" />
                Bulk Import Pricing
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to import pricing for <span className="font-medium text-slate-700">{selectedClient?.code}</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">CSV File</Label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="h-10"
                  data-testid="import-file-input"
                />
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p className="text-sm font-medium text-slate-700">CSV Format:</p>
                <code className="block bg-white border border-slate-200 p-3 rounded text-xs font-mono text-slate-600">
                  product_id,product_name,unit_price,notes
                </code>
                <p className="text-xs text-slate-500">
                  Existing pricing will be updated if product_id matches.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkImport}
                disabled={isLoading || !importFile}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-import-btn"
              >
                {isLoading ? 'Importing...' : 'Import'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default ClientPricingManager;
